import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Shield, Upload, CheckCircle, Circle, Clock, AlertCircle, FileText } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const BackgroundCheckPage = () => {
  const { user } = useAuth();
  const [checkStatus, setCheckStatus] = useState('pending');
  const [documents, setDocuments] = useState({ id_verification: false, address_proof: false, criminal_check: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getBackgroundCheck();
        setCheckStatus(data.status || 'pending');
        if (data.documents) setDocuments(data.documents);
      } catch {} finally { setLoading(false); }
    };
    load();
  }, [user]);

  const handleSubmit = async () => {
    try { await api.submitBackgroundCheck({ id_verification: documents.id_verification, address_proof: documents.address_proof, criminal_check_consent: documents.criminal_check }); } catch {}
    setCheckStatus('in_progress');
    toast({ title: 'Background check submitted', description: 'We\'ll process your verification and notify you when complete.' });
  };

  const steps = [
    { key: 'id_verification', title: 'Government ID', desc: 'Upload a passport or driver\'s license', required: true },
    { key: 'address_proof', title: 'Proof of Address', desc: 'Utility bill or bank statement (last 3 months)', required: true },
    { key: 'criminal_check', title: 'Background Check Consent', desc: 'Consent to a criminal background verification', required: false },
  ];

  const completedCount = Object.values(documents).filter(Boolean).length;
  const progress = Math.round((completedCount / steps.length) * 100);

  const statusColors = {
    pending: 'bg-gray-100 text-gray-800',
    in_progress: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-4xl mx-auto px-8 py-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Background Verification</h1>
              <p className="text-gray-600">Complete your background check to unlock premium opportunities</p>
            </div>
            <Badge className={statusColors[checkStatus] || statusColors.pending}>
              {checkStatus === 'pending' ? 'Not Started' : checkStatus === 'in_progress' ? 'In Progress' : checkStatus === 'approved' ? 'Verified' : 'Action Required'}
            </Badge>
          </div>

          {/* Progress */}
          <Card className="p-6 mb-8">
            <div className="flex items-center justify-between mb-3"><span className="text-sm font-medium text-gray-900">Verification Progress</span><span className="text-sm font-medium text-[#0A3900]">{progress}% complete</span></div>
            <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-[#0A3900] h-3 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} /></div>
          </Card>

          {checkStatus === 'approved' ? (
            <Card className="p-12 text-center">
              <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Complete!</h2>
              <p className="text-gray-600 mb-4">Your background check has been verified. You now have access to all premium features and high-value contracts.</p>
              <Badge className="bg-green-100 text-green-800 text-lg px-6 py-2">Verified Professional</Badge>
            </Card>
          ) : (
            <>
              {/* Document Steps */}
              <div className="space-y-4 mb-8">
                {steps.map((step) => (
                  <Card key={step.key} className={`p-6 ${documents[step.key] ? 'border-green-200 bg-green-50' : ''}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {documents[step.key] ? <CheckCircle className="w-6 h-6 text-green-600" /> : <Circle className="w-6 h-6 text-gray-300" />}
                        <div>
                          <h3 className="font-semibold text-gray-900">{step.title}</h3>
                          <p className="text-sm text-gray-600">{step.desc}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {step.required && !documents[step.key] && <Badge className="bg-red-100 text-red-800">Required</Badge>}
                        {documents[step.key] ? (
                          <Badge className="bg-green-100 text-green-800">Uploaded</Badge>
                        ) : (
                          <Button variant="outline" size="sm" onClick={() => setDocuments({ ...documents, [step.key]: true })}><Upload className="w-4 h-4 mr-2" />Upload</Button>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {checkStatus === 'in_progress' ? (
                <Card className="p-8 text-center">
                  <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Verification In Progress</h3>
                  <p className="text-gray-600">Your documents are being reviewed. This typically takes 2-3 business days.</p>
                </Card>
              ) : (
                <Button onClick={handleSubmit} className="bg-[#0A3900] hover:bg-[#1A5C0A] w-full py-3" disabled={completedCount < 2}>
                  <Shield className="w-5 h-5 mr-2" />Submit for Verification
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundCheckPage;
